export interface ValidationRule {
  type?: 'required' | 'email' | 'phone' | 'url' | 'number' | 'integer' | 'min' | 'max' | 'minLength' | 'maxLength' | 'pattern' | 'custom'
  value?: any
  message?: string
}

export interface ValidationResult {
  valid: boolean
  errors: string[]
}

export interface FieldValidation {
  field: string
  value: any
  rules: ValidationRule[]
}

export function validateRequired(value: any, message?: string): string | null {
  if (value === null || value === undefined || value === '') {
    return message || '此字段为必填项'
  }
  if (typeof value === 'string' && value.trim() === '') {
    return message || '此字段为必填项'
  }
  if (Array.isArray(value) && value.length === 0) {
    return message || '此字段为必填项'
  }
  return null
}

export function validateEmail(value: string, message?: string): string | null {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!regex.test(value)) {
    return message || '请输入有效的邮箱地址'
  }
  return null
}

export function validatePhone(value: string, message?: string): string | null {
  const regex = /^1[3-9]\d{9}$/
  if (!regex.test(value)) {
    return message || '请输入有效的手机号码'
  }
  return null
}

export function validateURL(value: string, message?: string): string | null {
  const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/
  if (!regex.test(value)) {
    return message || '请输入有效的URL地址'
  }
  return null
}

export function validateNumber(value: any, message?: string): string | null {
  if (isNaN(Number(value))) {
    return message || '请输入有效的数字'
  }
  return null
}

export function validateInteger(value: any, message?: string): string | null {
  if (!Number.isInteger(Number(value))) {
    return message || '请输入有效的整数'
  }
  return null
}

export function validateMin(value: number, min: number, message?: string): string | null {
  if (value < min) {
    return message || `最小值为 ${min}`
  }
  return null
}

export function validateMax(value: number, max: number, message?: string): string | null {
  if (value > max) {
    return message || `最大值为 ${max}`
  }
  return null
}

export function validateMinLength(value: string, min: number, message?: string): string | null {
  if (value.length < min) {
    return message || `最少 ${min} 个字符`
  }
  return null
}

export function validateMaxLength(value: string, max: number, message?: string): string | null {
  if (value.length > max) {
    return message || `最多 ${max} 个字符`
  }
  return null
}

export function validatePattern(value: string, pattern: RegExp, message?: string): string | null {
  if (!pattern.test(value)) {
    return message || '格式不正确'
  }
  return null
}

export function validateField(value: any, rules: ValidationRule[]): ValidationResult {
  const errors: string[] = []

  for (const rule of rules) {
    let error: string | null = null

    switch (rule.type) {
      case 'required':
        error = validateRequired(value, rule.message)
        break
      case 'email':
        error = validateEmail(value, rule.message)
        break
      case 'phone':
        error = validatePhone(value, rule.message)
        break
      case 'url':
        error = validateURL(value, rule.message)
        break
      case 'number':
        error = validateNumber(value, rule.message)
        break
      case 'integer':
        error = validateInteger(value, rule.message)
        break
      case 'min':
        error = validateMin(value, rule.value, rule.message)
        break
      case 'max':
        error = validateMax(value, rule.value, rule.message)
        break
      case 'minLength':
        error = validateMinLength(value, rule.value, rule.message)
        break
      case 'maxLength':
        error = validateMaxLength(value, rule.value, rule.message)
        break
      case 'pattern':
        error = validatePattern(value, rule.value, rule.message)
        break
      case 'custom':
        if (typeof rule.value === 'function') {
          error = rule.value(value)
        }
        break
    }

    if (error) {
      errors.push(error)
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

export function validateForm(fields: FieldValidation[]): {
  valid: boolean
  errors: Record<string, string[]>
} {
  const errors: Record<string, string[]> = {}
  let valid = true

  for (const field of fields) {
    const result = validateField(field.value, field.rules)
    if (!result.valid) {
      errors[field.field] = result.errors
      valid = false
    }
  }

  return { valid, errors }
}

export function createValidator(rules: Record<string, ValidationRule[]>) {
  return {
    validate: (data: Record<string, any>) => {
      const fields: FieldValidation[] = Object.entries(rules).map(([field, ruleList]) => ({
        field,
        value: data[field],
        rules: ruleList,
      }))
      return validateForm(fields)
    },
    validateField: (field: string, value: any) => {
      const ruleList = rules[field] || []
      return validateField(value, ruleList)
    },
  }
}
